// src/components/Layout.tsx
// App shell: floating glass topbar, ambient blob backdrop, animated nav.
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotifications } from '../hooks/index';
import { requestNotificationPermission } from './Toast';
import {
  IconDashboard, IconLive, IconBoard, IconFeed, IconMarket, IconUser,
  IconTransfer, IconYouth, IconClub, IconTrophy, IconAuction, IconDispute,
  IconVenue, IconWhistle, IconCloud, IconCard, IconBell, IconSun, IconMoon,
  IconMenu, IconClose, IconLogout, IconCricketBat, IconChevronLeft, IconChevronRight,
  IconWorld,
} from './icons';

type NavItem = { to: string; Icon: React.FC<any>; label: string; section: string; badge?: string };

const LISTINGS_ENABLED = process.env.REACT_APP_ENABLE_LISTINGS === 'true';

const NAV: NavItem[] = [
  { to: '/',            Icon: IconDashboard, label: 'Dashboard',      section: 'Overview' },
  { to: '/scoring',     Icon: IconLive,      label: 'Live Scoring',   section: 'Overview', badge: 'LIVE' },
  { to: '/scoreboard',  Icon: IconBoard,     label: 'Scoreboard',     section: 'Overview' },
  { to: '/world',       Icon: IconWorld,     label: 'World Cricket',  section: 'Overview' },
  { to: '/feed',        Icon: IconFeed,      label: 'Feed',           section: 'Overview' },
  { to: '/marketplace', Icon: IconMarket,    label: 'Marketplace',    section: 'Players' },
  ...(LISTINGS_ENABLED ? [{ to: '/looking-for', Icon: IconMarket, label: 'Looking For', section: 'Players' } as NavItem] : []),
  { to: '/profile',     Icon: IconUser,      label: 'My Profile',     section: 'Players' },
  { to: '/transfers',   Icon: IconTransfer,  label: 'Transfers',      section: 'Players' },
  { to: '/junior',      Icon: IconYouth,     label: 'Youth / Junior', section: 'Players' },
  { to: '/clubs/me',    Icon: IconClub,      label: 'My Club',        section: 'Club' },
  { to: '/tournaments', Icon: IconTrophy,    label: 'Tournaments',    section: 'Tournaments' },
  { to: '/auction',     Icon: IconAuction,   label: 'Auction Room',   section: 'Tournaments' },
  { to: '/disputes',    Icon: IconDispute,   label: 'Disputes',       section: 'Tournaments' },
  { to: '/venues',      Icon: IconVenue,     label: 'Venues',         section: 'Ground & Officials' },
  { to: '/umpires',     Icon: IconWhistle,   label: 'Umpires',        section: 'Ground & Officials' },
  { to: '/dls',         Icon: IconCloud,     label: 'DLS Calculator', section: 'Tools' },
  { to: '/payments',    Icon: IconCard,      label: 'Payments',       section: 'Tools' },
];

export default function Layout() {
  const { user, profile, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { unread } = useNotifications(user?.id);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Force-close the mobile drawer on every route change. Belt-and-braces
  // backstop in case a nav handler is bypassed (router-driven navigation,
  // browser back/forward, programmatic navigate()). Without this the
  // overlay can persist over the new page and block taps.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // useLiveAlerts was mounted here globally and subscribed to every
  // delivery INSERT + innings UPDATE row in the DB regardless of which
  // match the user is watching. That firehose churned every authed page
  // and stacked toasts from unrelated matches. The LiveScoring page now
  // owns scoped per-innings subscriptions via useDeliveriesLive /
  // useInningsLive, so the global wildcard listener is removed.

  const initials = profile?.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2)
    || user?.email?.slice(0, 2).toUpperCase()
    || 'U';

  const sections = Array.from(new Set(NAV.map(n => n.section)));
  const W = collapsed ? 72 : 248;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', color: 'var(--text-primary)', fontFamily: 'var(--font-body)', position: 'relative' }}>

      {/* Mobile overlay — render conditionally with NO AnimatePresence.
          framer-motion's exit animation keeps the overlay mounted with
          pointer-events: auto for ~300ms after mobileOpen flips false,
          which captured taps on the page the user just navigated to and
          made /scoring look "dimmed and unresponsive". Instant unmount
          here removes the dead overlay before the user's finger lands on
          the new page. */}
      {mobileOpen && (
        <div className="mobile-overlay" onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(3,8,5,0.72)', zIndex: 99 }} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'sidebar-open' : ''}`}
        style={{
          width: W, minHeight: '100vh',
          background: 'var(--glass-bg)',
          borderRight: '1px solid var(--glass-border)',
          // backdrop-filter blur is a major mobile compositor cost on Windows
          // Chrome and Android. Solid var(--glass-bg) keeps the visual identity
          // without the GPU hit; reintroduce per-page if a specific surface
          // truly needs glass.
          display: 'flex', flexDirection: 'column',
          position: 'fixed', top: 0, left: 0, zIndex: 100,
          transition: 'width 0.32s var(--ease-out-expo), transform 0.32s var(--ease-out-expo)',
          overflow: 'hidden',
        }}>

        {/* Logo */}
        <div style={{ padding: collapsed ? '18px 12px' : '20px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', gap: 8 }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 9,
                background: 'var(--brand-ink)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--brand-champagne)',
                border: '1px solid var(--hairline)',
                flexShrink: 0,
              }}>
                <IconCricketBat size={19} strokeWidth={1.7} />
              </div>
              <div>
                <div className="cl-serif" style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1, color: 'var(--text-primary)' }}>
                  CricLocal
                </div>
                <div className="cl-eyebrow" style={{ marginTop: 3 }}>Est. The Local Game</div>
              </div>
            </div>
          )}
          {collapsed && (
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--brand-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-champagne)', border: '1px solid var(--hairline)' }}>
              <IconCricketBat size={19} strokeWidth={1.7} />
            </div>
          )}
          {!collapsed && (
            <button onClick={() => { setCollapsed(true); setMobileOpen(false); }} style={iconBtn} title="Collapse sidebar">
              <IconChevronLeft size={16} />
            </button>
          )}
        </div>
        {collapsed && (
          <button onClick={() => setCollapsed(false)} style={{ ...iconBtn, width: '100%', borderRadius: 0, borderBottom: '1px solid var(--border-subtle)' }} title="Expand sidebar">
            <IconChevronRight size={16} />
          </button>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: collapsed ? '10px 8px' : '12px 12px', overflowY: 'auto', overflowX: 'hidden' }}>
          {sections.map(section => (
            <div key={section}>
              {!collapsed && <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1.8, textTransform: 'uppercase', padding: '14px 8px 6px' }}>{section}</div>}
              {collapsed && <div style={{ height: 1, background: 'var(--border-subtle)', margin: '10px 0' }} />}
              {NAV.filter(n => n.section === section).map(item => (
                <NavLink key={item.to} to={item.to} end={item.to === '/'} title={collapsed ? item.label : undefined}
                  onClick={() => setMobileOpen(false)}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 11,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    padding: collapsed ? '10px 0' : '10px 12px', borderRadius: 11, marginBottom: 2,
                    textDecoration: 'none', fontSize: 13.5, fontWeight: 500,
                    border: '1px solid transparent',
                    borderLeft: isActive ? '1px solid var(--brand-champagne)' : '1px solid transparent',
                    background: isActive ? 'var(--tint-forest)' : 'transparent',
                    color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                    boxShadow: 'none',
                    transition: 'all 0.18s var(--ease-out-expo)',
                  })}>
                  <span style={{ width: collapsed ? 'auto' : 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.Icon size={18} />
                  </span>
                  {!collapsed && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span className="cl-mono" style={{ background: 'transparent', color: 'var(--signal-live)', fontSize: 9, fontWeight: 700, padding: '1px 5px', border: '1px solid var(--signal-live)', borderRadius: 4, letterSpacing: 1.2 }}>{item.badge}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User */}
        <div style={{ padding: collapsed ? '12px 8px' : '14px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--bg-surface-alt)',
            border: '1px solid var(--hairline)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 12, color: 'var(--brand-champagne)', flexShrink: 0,
            letterSpacing: 0.5,
          }}>{initials}</div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-primary)' }}>{profile?.displayName || user?.email}</div>
              <div className="cl-mono" style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'lowercase', letterSpacing: 0.3, marginTop: 2 }}>
                {profile?.clubRole || 'Player'}{profile?.clubName ? ` · ${profile.clubName}` : ''}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="main-content" style={{ marginLeft: W, flex: 1, display: 'flex', flexDirection: 'column', transition: 'margin-left 0.32s var(--ease-out-expo)', position: 'relative', zIndex: 1 }}>

        {/* Floating glass topbar */}
        <div style={{ position: 'sticky', top: 12, zIndex: 50, padding: '0 16px', marginTop: 12 }}>
          <div className="cl-glass" style={{
            height: 56,
            borderRadius: 'var(--radius-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 14px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.18), 0 1px 0 var(--glass-highlight) inset',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="hamburger" onClick={() => { setCollapsed(false); setMobileOpen(!mobileOpen); }} style={{ ...iconBtn, display: 'none' }}>
                {mobileOpen ? <IconClose size={20} /> : <IconMenu size={20} />}
              </button>
              <div className="cl-mono" style={{
                display: 'flex', alignItems: 'center', gap: 7,
                background: 'transparent', border: '1px solid var(--signal-live)',
                padding: '3px 10px', borderRadius: 'var(--radius-pill)',
                fontSize: 10, fontWeight: 600, color: 'var(--signal-live)', letterSpacing: 1.5,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-live)', animation: 'pulse 1.6s ease-in-out infinite' }} />
                LIVE
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={toggleTheme} style={iconBtn} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
                {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <button className="topbar-btn-hide-mobile" onClick={() => navigate('/dls')} style={{ ...pillBtn }}>
                <IconCloud size={15} /> <span>DLS</span>
              </button>
              <button onClick={async () => { await requestNotificationPermission(); navigate('/notifications'); }} style={{ ...iconBtn, position: 'relative' }} title="Notifications & live alerts">
                <IconBell size={18} />
                {unread > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    background: 'var(--brand-red)', color: '#fff',
                    fontSize: 9, fontWeight: 800, minWidth: 16, height: 16, padding: '0 4px',
                    borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 0 2px var(--bg-primary)',
                  }}>{unread > 9 ? '9+' : unread}</span>
                )}
              </button>
              <motion.button
                className="topbar-btn-hide-mobile"
                onClick={() => navigate('/scoring')}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 420, damping: 24 }}
                style={{
                  background: 'var(--brand-ink)', color: 'var(--brand-bone)',
                  border: '1px solid var(--brand-champagne)',
                  padding: '7px 16px', borderRadius: 10,
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  letterSpacing: 0.1,
                }}
              >
                Score Now
              </motion.button>
              <button onClick={logout} style={{ ...iconBtn, color: '#F87171', borderColor: 'rgba(220,38,38,0.30)' }} title="Logout">
                <IconLogout size={17} />
              </button>
            </div>
          </div>
        </div>

        <main style={{ flex: 1, padding: '20px 16px 40px', maxWidth: 1200, width: '100%' }}>
          <Outlet />
        </main>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .sidebar { transform: translateX(-100%); width: 272px !important; }
          .sidebar.sidebar-open { transform: translateX(0) !important; width: 272px !important; }
          .main-content { margin-left: 0 !important; }
          .hamburger { display: inline-flex !important; }
          .topbar-btn-hide-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-color)',
  borderRadius: 10,
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  padding: '7px 9px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background var(--duration-fast), color var(--duration-fast), border-color var(--duration-fast)',
};

const pillBtn: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-color)',
  borderRadius: 10,
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  padding: '6px 11px',
  fontSize: 12.5, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontFamily: 'var(--font-body)',
};
